<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from '@/composable/useI18n'
import { useEditSensorModal } from '@/composable/useEditSensorModal'
import IcClose from '@/components/Icons/IcClose.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import { modalTransition } from '@/plugins/transitions'
import { SensorType } from '@/interfaces/enums'
import EditSensorBasicFields from '@/components/Sensors/Forms/EditSensorBasicFields.vue'
import EditEnergyFields from '@/components/Sensors/Forms/EditEnergyFields.vue'
import EditDoorOpenFields from '@/components/Sensors/Forms/EditDoorOpenFields.vue'
import EditTemperatureFields from '@/components/Sensors/Forms/EditTemperatureFields.vue'

const { t } = useI18n()

const props = defineProps<{
  isOpen: boolean
  sensorName: string
  sensorId: number
}>()

const emit = defineEmits(['close', 'saved'])

const { formData, errors, isLoading, sensorType, loadSensorData, saveSensor } = useEditSensorModal()

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue && props.sensorId) {
      loadSensorData(props.sensorId)
    }
  },
  { immediate: true },
)

function cancel() {
  emit('close')
}

const handleSave = async () => {
  await saveSensor(props.sensorId, emit)
}
</script>

<template>
  <transition v-bind="modalTransition">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="mx-auto flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden bg-white shadow-4">
        <div class="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 class="text-xl font-semibold">{{ t('globals.buttons.edit') }} Sensor</h2>
          <button @click="cancel" class="hover:text-slate-400">
            <IcClose />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-6">
          <form @submit.prevent="handleSave" class="space-y-6">
            <!-- Campos básicos do sensor -->
            <EditSensorBasicFields v-model="formData" :errors="errors" />

            <!-- Campos específicos por tipo de sensor -->
            <div v-if="sensorType">
              <EditEnergyFields v-if="sensorType === SensorType.ELECTRICITY" v-model="formData" />

              <EditDoorOpenFields v-else-if="sensorType === SensorType.DOOR_OPEN" v-model="formData" />

              <EditTemperatureFields v-else-if="sensorType === SensorType.TEMPERATURE" v-model="formData" />
            </div>

            <div class="flex justify-end space-x-4 border-t border-gray-200 pt-4">
              <button
                type="submit"
                class="bg-blue px-4 py-2 text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                <span v-if="isLoading">
                  <ButtonLoader />
                </span>
                <span v-else> Salvar Alterações </span>
              </button>
              <button @click="cancel" type="button" class="text-slate-500 hover:text-slate-400">
                {{ t('globals.buttons.cancel') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>
